/** Last identifier used for each login category (business client, private asset owner, …). */
export const LAST_LOGIN_IDS_BY_OPTION_KEY = 'ofrontend.lastLoginIdentifiersByOption';

/** @deprecated Per-path map conflated categories that share `/login/clients` or `/login/assetsowners`. */
export const LEGACY_LAST_LOGIN_IDS_BY_PATH_KEY = 'ofrontend.lastLoginIdentifiersByPath';

export const LAST_LOGIN_OPTION_KEY = 'ofrontend.lastLoginOption';
export const LAST_LOGIN_BUNDLE_KEY = 'ofrontend.lastLoginBundle';
export const LEGACY_LAST_LOGIN_IDENTIFIER_KEY = 'ofrontend.lastLoginIdentifier';

function readJsonStringMap(key: string): Record<string, string> {
  try {
    if (typeof localStorage === 'undefined') {
      return {};
    }
    const raw = localStorage.getItem(key);
    if (!raw?.trim()) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string' && k.trim()) {
        const trimmed = v.trim();
        if (trimmed) {
          out[k.trim()] = trimmed;
        }
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeJsonStringMap(key: string, map: Record<string, string>): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

export function readIdentifiersByLoginOption(): Record<string, string> {
  return readJsonStringMap(LAST_LOGIN_IDS_BY_OPTION_KEY);
}

function readLegacyIdentifiersByPath(): Record<string, string> {
  return readJsonStringMap(LEGACY_LAST_LOGIN_IDS_BY_PATH_KEY);
}

/** One-time: seed per-option map from bundle + legacy path when last option matches. */
let legacyLoginDefaultsMigrationDone = false;

export function migrateLegacyLoginDefaultsIfNeeded(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (Object.keys(readIdentifiersByLoginOption()).length > 0) {
    return;
  }
  const bundle = readLastLoginBundle();
  if (bundle) {
    persistIdentifierForLoginOption(bundle.option, bundle.identifier);
    return;
  }
  const lastOpt = localStorage.getItem(LAST_LOGIN_OPTION_KEY)?.trim();
  if (!lastOpt) {
    return;
  }
  const legacyPathMap = readLegacyIdentifiersByPath();
  const legacyId = Object.values(legacyPathMap).find((v) => v.trim());
  if (legacyId) {
    persistIdentifierForLoginOption(lastOpt, legacyId);
  }
}

function readLastLoginBundle(): { option: string; identifier: string } | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const raw = localStorage.getItem(LAST_LOGIN_BUNDLE_KEY);
    if (!raw?.trim()) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const o = parsed as Record<string, unknown>;
    const option = o.option;
    const identifier = o.identifier;
    if (typeof option === 'string' && typeof identifier === 'string' && identifier.trim()) {
      return { option: option.trim(), identifier: identifier.trim() };
    }
    return null;
  } catch {
    return null;
  }
}

export function readLastLoginOption(validOptions: readonly string[]): string | null {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    const raw = localStorage.getItem(LAST_LOGIN_OPTION_KEY);
    const t = raw?.trim();
    if (!t || !validOptions.includes(t)) {
      return null;
    }
    return t;
  } catch {
    return null;
  }
}

export function persistLastLoginOption(option: string): void {
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(LAST_LOGIN_OPTION_KEY, option);
  } catch {
    /* quota / private mode */
  }
}

export function persistLastLoginBundle(option: string, identifier: string): void {
  const id = identifier.trim();
  if (!id) {
    return;
  }
  try {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(LAST_LOGIN_BUNDLE_KEY, JSON.stringify({ option, identifier: id }));
  } catch {
    /* quota / private mode */
  }
}

/** Persist identifier for this login category; also updates bundle + last option. */
export function persistIdentifierForLoginOption(option: string, identifier: string): void {
  const t = identifier.trim();
  if (!t || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(LEGACY_LAST_LOGIN_IDENTIFIER_KEY);
    const map = readIdentifiersByLoginOption();
    map[option] = t;
    writeJsonStringMap(LAST_LOGIN_IDS_BY_OPTION_KEY, map);
    persistLastLoginOption(option);
    persistLastLoginBundle(option, t);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Last identifier for a login category. Migrates from bundle once when present; ignores legacy per-path map.
 */
export function readStoredIdentifierForLoginOption(option: string): string | null {
  if (!legacyLoginDefaultsMigrationDone) {
    legacyLoginDefaultsMigrationDone = true;
    migrateLegacyLoginDefaultsIfNeeded();
  }
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }
    localStorage.removeItem(LEGACY_LAST_LOGIN_IDENTIFIER_KEY);

    const direct = readIdentifiersByLoginOption()[option];
    if (direct) {
      return direct;
    }

    const bundle = readLastLoginBundle();
    if (bundle && bundle.option === option) {
      persistIdentifierForLoginOption(option, bundle.identifier);
      return bundle.identifier;
    }

    return null;
  } catch {
    return null;
  }
}

export function resolveLoginFormIdentifier(args: {
  option: string;
  sessionLoginType: string | null;
  sessionLoginIdentifier: string | null;
  demoIdentifier: string;
}): string {
  const stored = readStoredIdentifierForLoginOption(args.option);
  const sessionMatch =
    args.sessionLoginType === args.option && args.sessionLoginIdentifier?.trim()
      ? args.sessionLoginIdentifier.trim()
      : null;
  return stored ?? sessionMatch ?? args.demoIdentifier;
}
